import React from "react";
import { 
  Layers, 
  DollarSign, 
  TrendingUp,
  FileText,
  Download,
  History
} from "lucide-react";
import { Button } from "@/components/ui/button";
import StatsCard from "@/components/common/StatsCard";
import QuoteTypesTable from "@/components/sales/QuoteTypesTable";
import QuoteCalculator from "@/components/sales/QuoteCalculator";
import UploadSection from "@/components/sales/UploadSection";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

// Format utilities
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);
};

const formatNumber = (value, decimals = 2) => {
  return new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

/**
 * SalesView - Main Sales/Price Index dashboard view
 * Fully refactored component that encapsulates the sales section
 */
const SalesView = ({
  // Dashboard stats
  dashboardStats,
  
  // Upload section props
  onParseQuoteFile,
  onManualAdd,
  
  // Calculator props
  quoteTypes,
  selectedType,
  onTypeChange,
  materials,
  aluminumSeries,
  
  // Table props
  onDelete,
  onDeleteMultiple,
  onViewDetail,
  onSelectForCalculator,
  onAddQuote,
  onViewTrend,
  onDuplicate,
  onPriceAdjust,
  onOpenHistory,
  selectedTypeIds,
  onSelectionChange,
  onExport,
  onImagePreview,
  availableFactories,
  
  // Project filter
  projectFilter,
  onProjectFilterChange,
  allProjects,
  
  // Glass and Extras filters (NEW)
  glassFilter,
  onGlassFilterChange,
  availableGlassSpecs,
  extrasFilter,
  onExtrasFilterChange,
  availableExtrasSpecs,
  
  // PDF Export (NEW)
  onOpenPDFExport,
  
  // Quote History (NEW)
  onOpenQuoteHistory,
  
  // Refresh callback for data reload after edits
  onRefresh
}) => {
  return (
    <div className="space-y-8" data-testid="sales-view">
      {/* Header with Export Buttons */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Lista de Precio Venta</h2>
          <p className="text-sm text-slate-500">Gestión de tipos y cotizaciones</p>
        </div>
        <div className="flex items-center gap-2">
          {onOpenQuoteHistory && (
            <Button 
              variant="outline"
              onClick={onOpenQuoteHistory}
              className="gap-2"
              data-testid="quote-history-btn"
            >
              <History className="w-4 h-4" />
              Historial
            </Button>
          )}
          {onOpenPDFExport && (
            <Button 
              onClick={onOpenPDFExport}
              className="gap-2"
              data-testid="export-pdf-btn"
            >
              <FileText className="w-4 h-4" />
              Exportar PDF
            </Button>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="bento-grid">
        <div className="bento-item-third">
          <StatsCard
            icon={Layers}
            label="Tipos Indexados"
            value={dashboardStats?.total_types || 0}
            subValue={`${dashboardStats?.total_entries || 0} muestras totales`}
          />
        </div>
        <div className="bento-item-third">
          <StatsCard
            icon={DollarSign}
            label="Promedio General"
            value={formatCurrency(dashboardStats?.overall_average_cost_per_m2 || 0)}
            subValue="por m²"
          />
        </div>
        <div className="bento-item-third">
          <StatsCard
            icon={TrendingUp}
            label="Área Total Indexada"
            value={`${formatNumber(dashboardStats?.total_area_indexed || 0)} m²`}
            subValue="de cotizaciones procesadas"
          />
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="bento-grid">
        {/* Upload Section for standard quote files */}
        <div className="bento-item-half">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Cargar Cotización Estándar</CardTitle>
              <CardDescription>
                Archivos Excel/PDF de fábrica para calcular índice de precios
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* WindoorCraft moved to Cotización section */}
              <UploadSection 
                onParseQuoteFile={onParseQuoteFile}
                onManualAdd={onManualAdd}
              />
            </CardContent>
          </Card>
        </div>

        {/* Calculator Section */}
        <div className="bento-item-half">
          <QuoteCalculator 
            quoteTypes={quoteTypes}
            selectedType={selectedType}
            onTypeChange={onTypeChange}
            materials={materials}
            aluminumSeries={aluminumSeries}
          />
        </div>

        {/* Price Index Table */}
        <div className="bento-item-full">
          <QuoteTypesTable
            quoteTypes={quoteTypes}
            onDelete={onDelete}
            onDeleteMultiple={onDeleteMultiple}
            onViewDetail={onViewDetail}
            onSelectForCalculator={onSelectForCalculator}
            onAddQuote={onAddQuote}
            onViewTrend={onViewTrend}
            onDuplicate={onDuplicate}
            onPriceAdjust={onPriceAdjust}
            onOpenHistory={onOpenHistory}
            selectedTypeIds={selectedTypeIds}
            onSelectionChange={onSelectionChange}
            materials={materials}
            aluminumSeries={aluminumSeries}
            onExport={onExport}
            onImagePreview={onImagePreview}
            availableFactories={availableFactories}
            projectFilter={projectFilter}
            onProjectFilterChange={onProjectFilterChange}
            allProjects={allProjects}
            glassFilter={glassFilter}
            onGlassFilterChange={onGlassFilterChange}
            availableGlassSpecs={availableGlassSpecs}
            extrasFilter={extrasFilter}
            onExtrasFilterChange={onExtrasFilterChange}
            availableExtrasSpecs={availableExtrasSpecs}
            onRefresh={onRefresh}
          />
        </div>
      </div>
    </div>
  );
};

export default SalesView;
